import type { BusinessUnitOption } from "../types/businessUnit";

/** Synthetic BU used only in UI for “all units”; never persisted as a real ``code``. */
export const ALL_BUSINESS_UNITS_SENTINEL: BusinessUnitOption = {
  code: "__ALL__",
  name: "All BUs",
} as const;

/** Prepend ``All BUs`` sentinel when missing (API does not return it). */
export function mergeBusinessUnitOptionsFromApi(
  api: readonly BusinessUnitOption[]
): readonly BusinessUnitOption[] {
  const hasSentinel = api.some(
    (o) => o.code.toUpperCase() === ALL_BUSINESS_UNITS_SENTINEL.code.toUpperCase()
  );
  if (hasSentinel) {
    return [...api];
  }
  return [ALL_BUSINESS_UNITS_SENTINEL, ...api];
}

const isAllSelection = (selected: readonly string[]): boolean =>
  selected.some(
    (s) =>
      s === ALL_BUSINESS_UNITS_SENTINEL.name ||
      s === ALL_BUSINESS_UNITS_SENTINEL.code ||
      s.toLowerCase() === "all bus" ||
      s.toLowerCase() === "all bus." ||
      s.toLowerCase() === "all buss"
  );

/**
 * Map UI selection (chip / multi-select values, usually **names**) to **codes** for APIs.
 * Accepts legacy **codes** and mixed payloads from older section rows.
 */
export function resolveBusinessUnitsForApi(
  selected: readonly string[],
  options: readonly BusinessUnitOption[]
): string[] {
  const real = options.filter((o) => o.code !== ALL_BUSINESS_UNITS_SENTINEL.code);
  if (selected.length === 0) {
    return [];
  }
  if (isAllSelection(selected)) {
    return real.map((o) => o.code);
  }
  return selected.map((raw) => {
    const s = raw.trim();
    if (!s) {
      return raw;
    }
    const byCode = real.find((o) => o.code.toUpperCase() === s.toUpperCase());
    if (byCode) {
      return byCode.code;
    }
    const byName = real.find((o) => o.name.toLowerCase() === s.toLowerCase());
    return byName?.code ?? raw;
  });
}

/** First matching ``name`` for a stored **code** or legacy display string. */
export function businessUnitDisplayName(
  value: string,
  options: readonly BusinessUnitOption[]
): string {
  const s = value.trim();
  if (!s) {
    return value;
  }
  const real = options.filter((o) => o.code !== ALL_BUSINESS_UNITS_SENTINEL.code);
  const byCode = real.find((o) => o.code.toUpperCase() === s.toUpperCase());
  if (byCode) {
    return byCode.name;
  }
  const byName = real.find((o) => o.name.toLowerCase() === s.toLowerCase());
  return byName?.name ?? value;
}
